import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, BookOpen } from 'lucide-react';
import { blogs } from '../data/blogs';

const Blogs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Group blogs by series
  const seriesBlogs = blogs.filter(b => b.series);
  const standaloneBlogs = blogs.filter(b => !b.series);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] pt-24 pb-20">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label">INSIGHTS & EXPERTISE</p>
          <h1 className="section-title">Engineering Blog</h1>
          <p className="section-description mx-auto">
            Deep dives into automotive software engineering, Android platform development, 
            and emerging technologies from our team of experts.
          </p>
        </div>

        {/* Featured Series */}
        {seriesBlogs.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="text-[var(--accent-primary)]" size={24} />
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                Android Hardware Integration Series
              </h2>
            </div>
            <p className="text-[var(--text-secondary)] mb-8 max-w-3xl">
              A comprehensive 6-part guide covering the complete journey from Android app to physical hardware. 
              Learn how requests flow through each layer of the Android platform stack.
            </p>

            <div className="grid gap-4">
              {seriesBlogs.map((blog) => (
                <Link 
                  key={blog.id}
                  to={`/blog/${blog.id}`}
                  className="group flex items-start gap-6 p-6 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-xl hover:border-[var(--accent-primary)] transition-all duration-300"
                  data-testid={`blog-card-${blog.id}`}
                >
                  {/* Part Number */}
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[var(--accent-primary)] text-white flex items-center justify-center font-bold text-lg">
                    {blog.partNumber}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {blog.tags.slice(0, 4).map((tag) => (
                        <span 
                          key={tag}
                          className="text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--bg-secondary)] text-[var(--text-muted)]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-lg font-bold text-[var(--text-primary)] group-hover:text-[var(--accent-primary)] transition-colors mb-1">
                      {blog.title.split(' - ')[1] || blog.title}
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] mb-3 line-clamp-2">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {blog.readTime}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 self-center">
                    <ArrowRight 
                      size={20} 
                      className="text-[var(--text-muted)] group-hover:text-[var(--accent-primary)] group-hover:translate-x-1 transition-all" 
                    />
                  </div>
                </Link>
              ))}
            </div>

            {/* Start Reading CTA */}
            <div className="mt-8 text-center">
              <Link 
                to={`/blog/${seriesBlogs[0]?.id}`}
                className="btn-primary inline-flex items-center gap-2"
              >
                Start Reading Part 1
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}

        {/* Standalone Blogs */}
        {standaloneBlogs.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-8">
              More Articles
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {standaloneBlogs.map((blog) => (
                <Link 
                  key={blog.id}
                  to={`/blog/${blog.id}`}
                  className="group block bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-xl overflow-hidden hover:border-[var(--accent-primary)] transition-all duration-300 hover:shadow-lg"
                  data-testid={`blog-card-${blog.id}`}
                >
                  <div className="p-6 border-b border-[var(--border-subtle)] bg-gradient-to-r from-[var(--accent-subtle)] to-transparent">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {blog.tags.slice(0, 3).map((tag) => (
                        <span 
                          key={tag}
                          className="text-xs font-medium px-2 py-1 rounded-full bg-[var(--accent-primary)] text-white"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-xl font-bold text-[var(--text-primary)] group-hover:text-[var(--accent-primary)] transition-colors line-clamp-2">
                      {blog.title}
                    </h2>
                  </div>

                  <div className="p-6">
                    <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-3">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[var(--text-muted)] mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {blog.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {blog.readTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[var(--accent-primary)] font-medium text-sm group-hover:gap-3 transition-all">
                      Read Article
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-[var(--accent-subtle)] to-transparent border border-[var(--border-subtle)] rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-[var(--text-primary)] mb-3">
              Want to learn more about our expertise?
            </h3>
            <p className="text-[var(--text-secondary)] mb-6">
              Our team specializes in Android Automotive, embedded systems, and AI-enabled engineering solutions.
            </p>
            <Link 
              to="/#contact" 
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => {
                setTimeout(() => {
                  const element = document.querySelector('#contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              Discuss Your Project
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs;
